import { gsap, ScrollTrigger, SplitText } from "@gsap";

// Subtle text and scroll motion (same timings as Nova / onelane.nl).
// Skipped: sliders, dialogs, the header, forms, AJAX-swapped lists, inner scroll areas (ScrollTrigger follows the page),
// elements with their own animation (data-reveal, intro_content) and anything inside data-animate="none".
const SKIP = [
    ".swiper-slide",
    "dialog",
    ".pk-header",
    ".gform_wrapper",
    "[aria-live]",
    "[data-results]",
    ".location-list",
    "[data-reveal]",
    "[data-animate='none']",
    ".intro_content-layout",
].join(", ");

const START = "top 80%";

const usable = (selector) => gsap.utils.toArray(selector).filter((el) => !el.closest(SKIP));

// Hide now, reveal once per group when it scrolls into view.
const batch = (targets, from, to, { start = START, stagger = 0 } = {}) => {
    if (!targets.length) return;

    gsap.set(targets, from);
    ScrollTrigger.batch(targets, {
        start,
        once: true,
        onEnter: (group) => gsap.to(group, { ...to, stagger, clearProps: "transform,opacity,visibility" }),
    });
};

// H1: word by word. A hero title is in view on load, so it plays straight away.
const animateTitles = () => {
    usable(".flex-layout h1").forEach((title) => {
        const split = SplitText.create(title, { type: "words", wordsClass: "text-word" });
        if (!split.words.length) return;

        gsap.from(split.words, {
            autoAlpha: 0,
            yPercent: 40,
            duration: 1,
            delay: 0.1,
            stagger: 0.1,
            ease: "back.out(1.5)",
            scrollTrigger: { trigger: title, start: "top bottom-=20%", once: true },
            onComplete: () => split.revert(),
        });
    });
};

// H2 and H3: fade up.
const animateHeadings = () => {
    batch(
        usable(".flex-layout h2, .flex-layout h3"),
        { autoAlpha: 0, y: 50 },
        { autoAlpha: 1, y: 0, duration: 1, ease: "power2.out" },
        { stagger: 0.3 }
    );
};

// Buttons per group, so two buttons next to each other come in one after the other. Heroes are already in view.
const animateButtons = () => {
    usable(".flex-layout:not(.hero-layout) .pk-button-group").forEach((group) => {
        batch(
            group.querySelectorAll(".pk-button"),
            { autoAlpha: 0, y: 40 },
            { autoAlpha: 1, y: 0, duration: 0.4, ease: "power2.out" },
            { stagger: 0.15 }
        );
    });
};

// Image columns grow slightly into place while scrolling.
const animateImages = () => {
    usable(".flex-layout:not(.hero-layout) .image-col").forEach((col) => {
        gsap.fromTo(col, { scale: 0.9 }, {
            scale: 1,
            ease: "none",
            scrollTrigger: { trigger: col, start: "top 90%", end: "top 60%", scrub: 1, once: true },
        });
    });
};

document.addEventListener("DOMContentLoaded", () => {
    gsap.matchMedia().add("(prefers-reduced-motion: no-preference)", () => {
        animateTitles();
        animateHeadings();
        animateButtons();
        animateImages();
    });
});
